import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";
import type {
    CharBlock as CharBlockValue,
    ImageChooserBlock as ImageChooserBlockValue,
    ToppersImageLinkBlock as ImageLinkBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    RichTextBlock as RichTextBlockValue,
    TableBlock as TableBlockValue,
} from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { ImageLinkBlock } from "../../blocks/ImageLinkBlock";
import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

/**
 * toppers LegalPage body assembly: dispatches every block type in
 * ``LegalPageBodyContent`` (the 11 Toolbox keys + legal-specific extras:
 * heading / paragraph / image / html / image_link / table) to its React
 * component, collapsing N ``{% include_block %}`` hydration roots into a
 * single React render tree.
 *
 * PER-BLOCK WRAPPER (load-bearing): ``legal_page.html`` rendered the
 * body with the whole-field ``{{ self.body }}``, which
 * Wagtail's ``StreamBlock.render_basic`` wraps as
 * ``<div class="w-block-{type} block-{type}">{child.render()}</div>`` per
 * top-level block. The toppers ``_legal-page.scss`` targets ``.block-image_link``
 * and ``.block-table`` / ``.block-table table`` (descendant selectors), so the
 * ``block-{type}`` wrapper is REQUIRED. We reproduce it by rendering each block
 * through its own one-element ``StreamField`` with ``tagName="div"`` +
 * ``attrs.className``.
 *
 * heading/paragraph/image/html: heading -> escaped text; paragraph/html ->
 * raw-injected serialized HTML to avoid thelabui's extra ``.rich-text``
 * wrapper; image -> ``<img>``.
 *
 * ``table`` is the pre-rendered Wagtail ``<table>`` HTML string, raw-injected
 * in a bare classless ``<div>`` -- ``.block-table table`` is a descendant
 * selector so it still matches under the extra container.
 */

const legalComponentsMap = {
    ...toolboxComponentsMap,
    // LegalPageContent-specific extras.
    heading: (block: { value: Immutable<CharBlockValue> }) => (
        <>{block.value}</>
    ),
    paragraph: (block: { value: Immutable<RichTextBlockValue> }) => (
        <div dangerouslySetInnerHTML={{ __html: block.value }} />
    ),
    image: (block: { value: Immutable<ImageChooserBlockValue> }) => (
        <img
            src={block.value.url}
            alt={block.value.default_alt_text}
            width={block.value.width}
            height={block.value.height}
            loading="lazy"
            decoding="async"
        />
    ),
    html: (block: { value: Immutable<RawHTMLBlockValue> }) => (
        <EditorContent>
            {(marker) => (
                <div
                    dangerouslySetInnerHTML={{ __html: block.value }}
                    {...marker}
                />
            )}
        </EditorContent>
    ),
    image_link: (block: { value: Immutable<ImageLinkBlockValue> }) => (
        <ImageLinkBlock value={block.value} />
    ),
    table: (block: { value: Immutable<TableBlockValue> }) => (
        <div dangerouslySetInnerHTML={{ __html: block.value }} />
    ),
} as const;

export const LegalPageBody = (
    props: Immutable<{
        page: Pick<templates.LegalPageTemplate["page"], "body">;
    }>,
): React.JSX.Element => {
    return (
        <>
            {props.page.body.value.map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={legalComponentsMap}
                />
            ))}
        </>
    );
};
